import { join } from 'node:path';
import z from 'zod';
import { GIT_IGNORED_DIR, getGitIgnoredPath } from '@wix/cli-core-definitions';

const wixAstroSiteConfigSchema = z.object({
  $schema: z.string().optional(),
  projectType: z.literal('Site').optional().default('Site'),
  appId: z.string(),
  siteId: z.string(),
  site: z
    .object({
      outputDirectory: z.union([
        z.string().min(1),
        z.union([
          z.object({
            client: z.string().min(1),
            server: z.string().min(1).optional(),
          }),
          z.object({
            client: z.string().min(1).optional(),
            server: z.string().min(1),
          }),
        ]),
      ]),
    })
    .optional(),
});

const wixAstroAppConfigSchema = z.object({
  $schema: z.string().optional(),
  projectType: z.literal('App'),
  appId: z.string(),
  projectId: z.string().optional(),
  codeIdentifier: z.string().optional(),
  namespace: z.string().optional(),
});

export const wixAstroConfigSchema = z.discriminatedUnion('projectType', [
  wixAstroSiteConfigSchema,
  wixAstroAppConfigSchema,
]);

export type WixAstroConfig = z.infer<typeof wixAstroConfigSchema>;

export const envSchema = z.object({
  WIX_SITE_ID: z.string().uuid().optional(),
});

export type Env = z.infer<typeof envSchema>;

export const ENV_FILE_NAME = '.env.local';
export const TRANSLATIONS_FILE_PATH = join('src', 'translations.json');
export const BACKEND_WORKER_COMPONENT_ID =
  'ed5f3d0e-7b79-4717-9c00-c4cd7bbbe906';

export const devMetadataSchema = z.object({
  staticsUrlPlaceholder: z.string(),
  serverUrlPlaceholder: z.string(),
});

export type DevMetadata = z.infer<typeof devMetadataSchema>;

const editorReactComponentTopologySchema = z.object({
  id: z.string().uuid(),
  type: z.literal('EDITOR_REACT_COMPONENT'),
  topology: z.object({
    'resources.client.componentUrl': z.object({
      distPath: z.string(),
      srcPath: z.string(),
    }),
  }),
});

const editorContextProviderTopologySchema = z.object({
  id: z.string().uuid(),
  type: z.literal('EDITOR_CONTEXT_PROVIDER'),
  topology: z.object({
    'resources.client.url': z.object({
      distPath: z.string(),
      srcPath: z.string(),
    }),
  }),
});

const editorFunctionLibraryTopologySchema = z.object({
  id: z.string().uuid(),
  type: z.literal('EDITOR_FUNCTION_LIBRARY'),
  topology: z.object({
    'resources.client.url': z.object({
      distPath: z.string(),
      srcPath: z.string(),
    }),
  }),
});

export type EditorReactComponentTopology = z.infer<
  typeof editorReactComponentTopologySchema
>;

const supportedExtensionTopologySchemas = [
  editorReactComponentTopologySchema,
  editorContextProviderTopologySchema,
  editorFunctionLibraryTopologySchema,
];

export type SupportedExtensionTopologyItem = z.infer<
  (typeof supportedExtensionTopologySchemas)[number]
>;

const rawExtensionTopologyItemSchema = z.object({
  id: z.string().uuid(),
  type: z.string(),
});

const extensionsTopologySchema = z
  .array(rawExtensionTopologyItemSchema.passthrough())
  .transform((items) =>
    items.flatMap((item) => {
      for (const schema of supportedExtensionTopologySchemas) {
        const result = schema.safeParse(item);
        if (result.success) {
          return [result.data];
        }
      }
      return [];
    })
  );

export type ExtensionsTopologySchema = z.infer<typeof extensionsTopologySchema>;

export const buildMetadataSchema = z.object({
  metaDataSource: z.literal('BUILD_METADATA').default('BUILD_METADATA'),
  appManifestPath: z.string(),
  clientDir: z.string().optional(),
  serverDir: z.string().optional(),
  staticsUrlPlaceholder: z.string().optional(),
  serverUrlPlaceholder: z.string().optional(),
  generateAppDeploymentGroupId: z.boolean().optional(),
  extensionsTopology: extensionsTopologySchema.optional(),
});

export const staticSiteBuildMetadaSchema = buildMetadataSchema.extend({
  metaDataSource: z.literal('WIX_CONFIG').default('WIX_CONFIG'),
  appManifestPath: z.string().optional(),
});

export type StaticSiteBuildMetadataSchema = z.infer<
  typeof staticSiteBuildMetadaSchema
>;

const deploymentTopologyEnvironmentSchema = z.object({
  id: z.string().describe('Unique ARM tag identifier'),
  site: z
    .object({
      main: z.string().url().describe('Main site URL'),
    })
    .describe('Site topology details')
    .optional(),
  dashboard: z
    .object({
      main: z
        .string()
        .url()
        .describe('Main dashboard URL (link to a business manager dashboard)'),
    })
    .describe('Optional dashboard topology details')
    .optional(),
  editor: z
    .object({
      main: z.string().url().describe('Main editor URL'),
    })
    .describe('Editor topology details')
    .optional(),
});

export type DeploymentTopologyEnvironmentSchema = z.infer<
  typeof deploymentTopologyEnvironmentSchema
>;

const deploymentTopologyEnvironmentTypeSchema = z.enum([
  'production',
  'preview',
  'development',
]);

export type DeploymentTopologyEnvironmentType = z.infer<
  typeof deploymentTopologyEnvironmentTypeSchema
>;

export const deploymentTopologySchema = z.record(
  deploymentTopologyEnvironmentTypeSchema,
  deploymentTopologyEnvironmentSchema
);

export type DeploymentTopology = z.infer<typeof deploymentTopologySchema>;

export function getBuildMetadataPath(projectFolder: string) {
  return join(projectFolder, GIT_IGNORED_DIR, 'build-metadata.json');
}

export function getDeploymentTopologyPath(projectFolder: string) {
  return getGitIgnoredPath(projectFolder, 'topology.json');
}

export function getEnvFilePath(projectFolder: string) {
  return join(projectFolder, ENV_FILE_NAME);
}

export function getExtensionDir(projectFolder: string) {
  return join(projectFolder, 'src', 'extensions');
}

const MULTILINGUAL_DIR = join(GIT_IGNORED_DIR, 'multilingual');
export const TRANSLATIONS_DIR = join(MULTILINGUAL_DIR, 'translations');
export const MULTILINGUAL_METADATA_FILENAME = 'metadata.json';

export function getTranslationsDir(projectFolder: string) {
  return join(projectFolder, TRANSLATIONS_DIR);
}

export function getMultilingualMetadataPath(projectFolder: string) {
  return join(projectFolder, MULTILINGUAL_DIR, MULTILINGUAL_METADATA_FILENAME);
}

export function getDebugLogPath(projectFolder: string) {
  return getGitIgnoredPath(projectFolder, 'debug-logs', 'cli-debug.log');
}

export const ASTRO_APP_CONFIG_PATH = join(GIT_IGNORED_DIR, 'app.config.json');

export const astroAppConfigSchema = z.object({
  siteId: z.string().uuid().optional(),
  devArmTag: z.string().optional(),
});

export type AstroAppConfig = z.infer<typeof astroAppConfigSchema>;

export function getAstroAppConfigPath(projectFolder: string) {
  return join(projectFolder, ASTRO_APP_CONFIG_PATH);
}
